import React, { FC, useState, useEffect } from 'react'

import { EuiFlyoutHeader, EuiFlyoutBody, EuiFlyoutFooter, EuiTitle, EuiButton, EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiFieldText, EuiSwitch, EuiTextArea, EuiCallOut, EuiSpacer } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import CustomMarkdownEditor from 'components/CustomMarkdownEditor'
import { MasterCampaignPlanCustomSection } from 'api/interfaces/MasterCampaignPlan'
import BlueprintEditWarning from 'features/planner/components/BlueprintEditWarning'

export const MARKET_ANALYSIS_CUSTOM_SECTION_EXAMPLE_CONTENT = `## Section Title

Provide an overview of the key points for this section. Include relevant details about market trends, competitive positioning, or strategic insights.

### Key Highlights

- **First point** - describe the primary finding or recommendation
- **Second point** - add supporting evidence or data
- **Third point** - note any implications or action items

### Additional Details

Expand on the highlights above with deeper analysis. Include specific metrics, market data, or strategic rationale that supports your conclusions.`

export interface EditCustomSectionFlyoutProps {
  section: MasterCampaignPlanCustomSection | null | undefined
  defaultTitle: string
  defaultContent: string
  defaultShowTitle: boolean
  flyoutTitle: string
  onSave: (title: string, content: string, showTitle: boolean) => void
  onClose: () => void
  isLoading?: boolean
  showBudgetWarning?: boolean
  hideTitleToggle?: boolean
  showBlueprintWarning?: boolean
  useMarkdownEditor?: boolean
  previewRenderer?: React.FC<{ content: string; className?: string }>
}

const EditCustomSectionFlyout: FC<EditCustomSectionFlyoutProps> = ({ section, defaultTitle, defaultContent, defaultShowTitle, flyoutTitle, onSave, onClose, isLoading = false, showBudgetWarning = false, hideTitleToggle = false, showBlueprintWarning = false, useMarkdownEditor = false, previewRenderer }) => {
  const [title, setTitle] = useState(section?.title ?? defaultTitle)
  const [content, setContent] = useState(section?.content ?? defaultContent)
  const [showTitle, setShowTitle] = useState(section?.showTitle ?? defaultShowTitle)

  useEffect(() => {
    setTitle(section?.title ?? defaultTitle)
    setContent(section?.content ?? defaultContent)
    setShowTitle(section?.showTitle ?? defaultShowTitle)
  }, [section, defaultTitle, defaultContent, defaultShowTitle])

  const handleSave = () => {
    onSave(title, content, showTitle)
  }

  const isSaveDisabled = content.trim().length === 0 || isLoading

  return (
    <AcFlyout onClose={onClose} size={useMarkdownEditor ? 'l' : 'm'}>
      <EuiFlyoutHeader>
        <EuiTitle size='m'>
          <h2>{flyoutTitle}</h2>
        </EuiTitle>
      </EuiFlyoutHeader>

      <EuiFlyoutBody style={useMarkdownEditor ? { overflow: 'hidden' } : undefined}>
        <BlueprintEditWarning show={showBlueprintWarning} />
        {showBudgetWarning && (
          <>
            <EuiCallOut title='Heads up!' color='warning' iconType='alert'>
              <p>If you customize this budget section, it becomes static and won't automatically update with campaign changes. Future edits to campaign budgets may cause the dollar amount and percentages here to become out of sync.</p>
            </EuiCallOut>
            <EuiSpacer size='m' />
          </>
        )}
        <EuiFormRow label='Title' fullWidth>
          <EuiFieldText value={title} onChange={e => setTitle(e.target.value)} fullWidth disabled={isLoading} />
        </EuiFormRow>

        {!hideTitleToggle && (
          <EuiFormRow fullWidth>
            <EuiSwitch label='Show Title' checked={showTitle} onChange={e => setShowTitle(e.target.checked)} disabled={isLoading} />
          </EuiFormRow>
        )}

        {useMarkdownEditor ? (
          <EuiFormRow label='Content' fullWidth>
            <CustomMarkdownEditor
              height='calc(100vh - 380px)'
              value={content}
              onChange={setContent}
              readOnly={isLoading}
              previewRenderer={previewRenderer}
              placeholder=''
            />
          </EuiFormRow>
        ) : (
          <EuiFormRow label='Content' fullWidth>
            <EuiTextArea value={content} onChange={e => setContent(e.target.value)} rows={15} fullWidth disabled={isLoading} />
          </EuiFormRow>
        )}
      </EuiFlyoutBody>

      <EuiFlyoutFooter>
        <EuiFlexGroup justifyContent='flexStart' gutterSize='l'>
          <EuiFlexItem grow={false}>
            <EuiButton onClick={onClose} disabled={isLoading}>
              Cancel
            </EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton fill onClick={handleSave} disabled={isSaveDisabled} isLoading={isLoading}>
              Save
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </AcFlyout>
  )
}

export default EditCustomSectionFlyout
